/* eslint-disable eqeqeq */
import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { handleTabToNext, looseEq, truncDec } from '../../formKit';
import { ahuBoardBus } from '../../../bus/ahuBoardBus';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm, type AccessoryFormApi } from '../useAccessoryForm';
import {
  ABareInput,
  AccessorySelectHeading,
  AccSubtab,
  accList,
  AutoSizeRow,
} from '../accessoryFieldKit';
import AccessoryMountingPopup, { type SpecialParametersProps } from '../AccessoryMountingPopup';
import { makeAutoSize } from '../useAutoSize';
import { useSectionInternalWalls } from '../internalWalls';
import {
  ComplexMountingPlaneRow,
  ComplexPlaneRows,
  resolveComplexPlane,
  type PlaneOption,
} from '../complexMounting';
import { AirDirectionRow, BladeDirectionRow } from './LouverForm';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-2 — DAMPER form (accessories/damper/{view/form.js 393,
 * tmpl/form.tmpl 651 — THE §52.1 grammar reference}, read in full). Built
 * TOGETHER with hood as one unit (#335). §51.2 damper row, THE WEB:
 *  - update:damper:params → rebuild cms_accessory (hood-constrained
 *    `dampers/params/?max_height&max_width&hood=true` when hood-mounted;
 *    plain `dampers/params/?` otherwise) — RTKQ URL cache + in-flight dedup
 *    = the legacy stateStorage `dampers_cms` + module pending map;
 *  - update:damper:fields → other dampers re-render their mounting list;
 *  - damper_change_params_<sectionId> → fan changed: refetch fan-intake CMS;
 *  - remove_damper_accessory_<sectionId> → fan_intake freed: refetch (the
 *    server recomputes allow_fan_intake; legacy flipped it client-side from
 *    the removed view's plane — payload not carried on the React bus);
 *  - damper:fan_intake:crossing → plane-9 takeover broadcast (positionModel
 *    emits after save — parity 351);
 *  - storage:accessory.position.model:change → cross-damper allow_fan_intake
 *    arbitration + own CMS swap;
 *  - hood:removed:from:section → reset to plain damper CMS (selector staging
 *    hood_id clear lands with the 6e2c selector);
 *  - internal_wall created/deleted/change → useSectionInternalWalls refetch;
 *  - dataGetter on_cabinet vs on_internal_wall plane resolution (shared
 *    internalWalls helper), strips internal_wall_* from the on-cabinet list,
 *    fan_intake stripped unless allow_fan_intake (parity 330);
 *  - fresh-create auto_open_collapse (parity 329) — mounting+performance
 *    subtabs auto-open for `label`-carrying fresh rows;
 *  - own SelectionMode handler (callOnDispatcher:true) → Select heading;
 *  - makeAutoSize ratio 2 over damper_params aw pair + faceVel 1200.
 */
const config = ACCESSORY_FORM_CONFIGS.damper;

const PLAIN_DAMPER_CMS = '/api/v1.0/cms/1/v1.0.0/ahu/dampers/params/?';
const FAN_INTAKE_CMS = '/api/v1.0/cms/1/v1.0.0/ahu/fans/dampers/params/';
const hoodCmsURL = (maxH: unknown, maxW: unknown) =>
  `/api/v1.0/cms/1/v1.0.0/ahu/dampers/params/?max_height=${maxH}&max_width=${maxW}&hood=true`;

const damperAutoSize = makeAutoSize({
  widthField: 'aw_width',
  heightField: 'aw_height',
  widthRatio: 2,
  getExisting: (model) => {
    try {
      const p = JSON.parse(String(model.get('damper_params') ?? '{}')) as Record<string, unknown>;
      return { existingWidth: p.aw_width, existingHeight: p.aw_height };
    } catch {
      return { existingWidth: null, existingHeight: null };
    }
  },
  applyResult: (data, width, height, modelJSON) => {
    const p = modelJSON.damper_params
      ? (JSON.parse(String(modelJSON.damper_params)) as Record<string, unknown>)
      : {};
    data.damper_params = JSON.stringify({ ...p, aw_width: width, aw_height: height });
  },
});

/** mounting_popup_special_parameters.tmpl (damper, 42 lines). */
const DamperSpecialParameters = ({ api, stageModelField, staged }: SpecialParametersProps) => {
  const planeValue = api.mounting?.plane_value;
  const casingMounts = accList(api.cmsAccessory, 'casing_mount');
  const airDirections = accList(api.cmsAccessory, 'air_direction');
  const bladeDirections = accList(api.cmsAccessory, 'blade_direction');
  const casingCurrent = staged.casing_mount_id ?? api.model?.get('casing_mount_id');
  const airCurrent = staged.air_direction_id ?? api.model?.get('air_direction_id');
  const bladeCurrent = staged.blade_direction_id ?? api.model?.get('blade_direction_id');
  return (
    <ul>
      {planeValue !== 'floor' && (
        <li className="special-parameter-casing_mount">
          <span className="label">Casing Mount</span>
          <div>
            {casingMounts.map((row) => (
              <label className="radio" key={String(row.id)}>
                <input
                  type="radio"
                  name="casing_mount_id"
                  value={String(row.id)}
                  checked={casingCurrent == row.id}
                  className="model_field_popup"
                  onChange={(e) => stageModelField('casing_mount_id', parseInt(e.target.value, 10))}
                />{' '}
                {/* §JSX-WS — damper mounting_popup_special_parameters.tmpl:14-15
                    newlines between the radio and `{{name}}`. */}
                {String(row.name ?? '')}
              </label>
            ))}
          </div>
        </li>
      )}
      <li className="special-parameter-air_direction">
        <span className="label">Air Direction</span>
        <select
          name="air_direction_id"
          className="model_field_popup"
          required
          value={airCurrent == null ? 'None' : String(airCurrent)}
          onChange={(e) => stageModelField('air_direction_id', parseInt(e.target.value, 10))}
        >
          <option value="None">- -</option>
          {airDirections.map((o) => (
            <option key={String(o.id)} value={String(o.id)}>
              {String(o.name ?? '')}
            </option>
          ))}
        </select>
      </li>
      <li className="special-parameter-blade_direction">
        <span className="label">Blade Direction</span>
        <select
          name="blade_direction_id"
          className="model_field_popup"
          required
          value={bladeCurrent == null ? 'None' : String(bladeCurrent)}
          onChange={(e) => stageModelField('blade_direction_id', parseInt(e.target.value, 10))}
        >
          <option value="None">- -</option>
          {bladeDirections.map((o) => (
            <option key={String(o.id)} value={String(o.id)}>
              {String(o.name ?? '')}
            </option>
          ))}
        </select>
      </li>
    </ul>
  );
};

const asJson = (m: unknown): Record<string, unknown> => {
  if (m && typeof (m as { toJSON?: unknown }).toJSON === 'function') {
    return (m as { toJSON(): Record<string, unknown> }).toJSON();
  }
  return (m as Record<string, unknown>) ?? {};
};

/** THE hood↔damper + fan_intake dispatcher web (§51.2 damper row / #335). */
function useDamperWeb(api: AccessoryFormApi, rowId: number, sectionId: number) {
  const dispatch = useAppDispatch();
  // boot CMS swap for hood-mounted dampers: the chassis hood-detection effect
  // fires update:damper:params in the SAME commit that registers the web
  // listeners below (legacy registered its dispatcher listeners in the ctor,
  // before the fetch chain) — drive the swap off the hoodIsMounting STATE so
  // ordering can't drop it.
  const bootSwapped = useRef(false);
  const refetchDetail = useCallback(async (): Promise<Record<string, unknown> | null> => {
    try {
      const fresh = await dispatch(
        ahuBoardApi.endpoints.getAccessoryDetail.initiate(
          { plural: 'dampers', id: rowId },
          { subscribe: false, forceRefetch: true }
        )
      ).unwrap();
      api.model?.set(fresh as Record<string, unknown>, { xhr: true });
      return fresh as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [dispatch, rowId, api.model]);

  useEffect(() => {
    const m = api.model;
    if (!m || !api.hoodIsMounting || bootSwapped.current) return;
    bootSwapped.current = true;
    void refetchDetail().then((fresh) => {
      const j = fresh ?? api.model?.toJSON() ?? {};
      api.setCmsURLOverride(hoodCmsURL(j.max_height, j.max_width));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.hoodIsMounting, api.model]);

  useEffect(() => {
    const m = api.model;
    if (!m) return undefined;
    const offs: Array<() => void> = [];

    // -- edge 1: update:damper:params — rebuild own CMS (hood-constrained /
    // plain) when the payload model IS this damper (§51.2 / form.js:172-205).
    offs.push(
      ahuBoardBus.on('update:damper:params', (...busArgs: unknown[]) => {
        const [payload, hoodIsMounting, modelIsHood] = busArgs as [
          unknown,
          boolean | undefined,
          boolean | undefined,
        ];
        const json = asJson(payload);
        if (json.id != rowId) return;
        if (modelIsHood) {
          api.markHoodIsMounting(true);
          api.setCmsURLOverride(hoodCmsURL(json.max_height, json.max_width));
        } else if (!hoodIsMounting) {
          void refetchDetail().then(() => {
            api.markHoodIsMounting(false);
            api.setCmsURLOverride(PLAIN_DAMPER_CMS);
          });
        } else {
          void refetchDetail().then((fresh) => {
            if (!fresh) return;
            api.markHoodIsMounting(true);
            api.setCmsURLOverride(hoodCmsURL(fresh.max_height, fresh.max_width));
          });
        }
      })
    );

    // -- edge 2: update:damper:fields — ANOTHER damper in the section changed
    // its mount: re-render own mounting list (renderDamperMountingList).
    offs.push(
      ahuBoardBus.on('update:damper:fields', (...busArgs: unknown[]) => {
        const json = asJson(busArgs[0]);
        if (json.section_id == m.get('section_id') && json.id !== m.get('id')) {
          void refetchDetail();
        }
      })
    );

    // -- edge 3: damper_change_params_<sectionId> — fan changed: fan-intake
    // dampers refetch the fan-constrained CMS (form.js:206-216).
    offs.push(
      ahuBoardBus.on(
        `damper_change_params_${sectionId}` as `damper_change_params_${number}`,
        (...busArgs: unknown[]) => {
          const fan = asJson(busArgs[0]);
          const pos = api.position;
          if (
            m.get('allow_fan_intake') &&
            String(pos?.get('mounting_plane') ?? '') === 'fan_intake' &&
            (fan.section_id == null || m.get('section_id') == fan.section_id)
          ) {
            void refetchDetail().then(() => {
              api.setIntakeMode(true);
              api.setCmsURLOverride(FAN_INTAKE_CMS);
            });
          }
        }
      )
    );

    // -- edge 4: remove_damper_accessory_<sectionId> — a damper was deleted:
    // refetch (server recomputes allow_fan_intake; legacy read the removed
    // view's plane from the payload the React bus does not carry).
    offs.push(
      ahuBoardBus.on(
        `remove_damper_accessory_${sectionId}` as `remove_damper_accessory_${number}`,
        () => {
          void refetchDetail();
        }
      )
    );

    // -- edge 5: damper:fan_intake:crossing — plane-9 takeover broadcast
    // (another damper crossed into/out of fan_intake — form.js:231-237).
    offs.push(
      ahuBoardBus.on('damper:fan_intake:crossing', (crossSectionId, damperId) => {
        if (crossSectionId !== Number(m.get('section_id'))) return;
        if (damperId === Number(m.get('id'))) return;
        void refetchDetail();
      })
    );

    // -- edge 6: storage:accessory.position.model:change — cross-damper
    // allow_fan_intake arbitration + own CMS swap (form.js:238-267).
    offs.push(
      ahuBoardBus.on('storage:accessory.position.model:change', (...busArgs: unknown[]) => {
        const [position, changed] = busArgs as [
          { get<T = unknown>(k: string): T },
          Record<string, unknown>,
        ];
        if (!changed || !Object.prototype.hasOwnProperty.call(changed, 'mounting_plane_id'))
          return;
        // 5e-sync — dev 5dc43d7222 (damper/view/form.js:240): the whole
        // mounting-plane auto-adjust is skipped under view-only permission
        // (no client-side allow_fan_intake flips / CMS swaps in view mode).
        if (document.body.classList.contains('ahu-view-permission')) return;
        const positionId = Number(position.get('id'));
        const planeIsFanIntake =
          Number(position.get('mounting_plane_id')) === 9 ||
          String(position.get('mounting_plane') ?? '') === 'fan_intake';
        const allowFanIntake = m.get('allow_fan_intake');
        if (m.id != positionId && planeIsFanIntake) {
          // NOTE: legacy PATCHes the OTHER damper's allow_fan_intake here;
          // the React set stays in-memory unless this form was user-touched
          // (data-safety gate) — the server recomputes it on next fetch.
          m.set({ allow_fan_intake: false });
        } else if (m.id != positionId && !planeIsFanIntake && allowFanIntake) {
          m.set({ allow_fan_intake: true });
        }
        if (m.id == positionId && planeIsFanIntake) {
          void refetchDetail().then(() => {
            api.setIntakeMode(true);
            api.setCmsURLOverride(FAN_INTAKE_CMS);
          });
        } else if (m.id == positionId && !planeIsFanIntake) {
          api.setIntakeMode(false);
          api.setCmsURLOverride(null);
        }
      })
    );

    // -- edge 7: hood:removed:from:section — reset to the plain damper CMS
    // (form.js:281-296; the selector staging hood_id clear lands with 6e2c).
    offs.push(
      ahuBoardBus.on('hood:removed:from:section', () => {
        api.markHoodIsMounting(false);
        void refetchDetail().then(() => {
          api.setCmsURLOverride(PLAIN_DAMPER_CMS);
        });
      })
    );

    // render:damper:mounting — hood moved/renamed: re-render mounting list.
    offs.push(
      ahuBoardBus.on('render:damper:mounting', () => {
        void refetchDetail();
      })
    );

    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.model, rowId, sectionId]);
}

/**
 * damper_params select row with selection mirror.
 *
 * ★ No `required` here. `form.tmpl:33/45/57/124` emit `<span class="label ">`
 * for Manufacturer/Series/Leakage/Frame Material — the trailing space is where a
 * modifier class would have gone and legacy never put one there, so none of the
 * four carries `.required`, whose `:after { content: "*" }` (common.css:29082)
 * is what draws the asterisk. The React port added it; the legacy selects have
 * no `required` attribute either. Louver is the same — see `LParamsSelect` in
 * LouverForm for the note on which accessory families do use that idiom.
 */
const DParamsSelect = ({
  api,
  name,
  label,
  cmsPath,
  labelKey = 'name',
}: {
  api: AccessoryFormApi;
  name: string;
  label: string;
  cmsPath: string;
  labelKey?: string;
}) => {
  const engine = api.catalogueFields.master_damper_id;
  const value = engine?.paramValue(name);
  const options = accList(api.cmsAccessory, cmsPath);
  const current = options.find((o) => looseEq(o.id, value));
  return (
    <li>
      <div className="single-line">
        <span className="selection">
          {current ? <span>{String(current[labelKey] ?? '')}</span> : null}
        </span>
        <select
          name={name}
          className="damper_params_field"
          value={value == null || value === '' ? 'None' : String(value)}
          onChange={(e) => api.commitParamsField('master_damper_id', name, e.target.value)}
        >
          <option value="None" className="hide">
            - -
          </option>
          {options.map((o) => (
            <option key={String(o.id)} value={String(o.id)}>
              {String(o[labelKey] ?? '')}
            </option>
          ))}
        </select>
        <span className="label">{label}</span>
      </div>
    </li>
  );
};

/** AW/OD four-input row (damper grammar reference — min/max hide classes). */
const AwOdRow = ({ api }: { api: AccessoryFormApi }) => {
  const engine = api.catalogueFields.master_damper_id;
  const model = api.model;
  const widths = accList(api.cmsAccessory, 'aw_width');
  const heights = accList(api.cmsAccessory, 'aw_height');
  const awW = engine?.paramValue('aw_width');
  const awH = engine?.paramValue('aw_height');
  const minW = Number(model?.get('min_width'));
  const maxW = Number(model?.get('max_width'));
  const minH = Number(model?.get('min_height'));
  const maxH = Number(model?.get('max_height'));
  const pick = (
    name: 'aw_width' | 'aw_height',
    list: typeof widths,
    value: unknown,
    min: number,
    max: number
  ) => (
    <>
      <span className="selection">
        {list.find((o) => looseEq(o[name], value)) ? <span>{truncDec(value)}</span> : null}
      </span>
      <select
        name={name}
        className="damper_params_field small-input"
        required
        value={value == null || value === '' ? 'None' : String(value)}
        onChange={(e) => api.commitParamsField('master_damper_id', name, e.target.value)}
      >
        <option value="None">- -</option>
        {list.map((o) => {
          const v = Number(o[name]);
          const hide = min ? min > v || (max ? max < v : false) : false;
          return (
            <option key={String(o[name])} value={String(o[name])} className={hide ? 'hide' : ''}>
              {truncDec(o[name])}
            </option>
          );
        })}
      </select>
    </>
  );
  return (
    <li>
      <div className="single-line  four-input">
        <span className="unit">in</span>
        {pick('aw_width', widths, awW, minW, maxW)}
        <span className="for_sep">/</span>
        <input
          className="small-input"
          type=""
          name="od_width"
          readOnly
          value={truncDec(model?.get('od_width'))}
          onChange={() => undefined}
        />
        <span className="for_sep">x</span>
        {pick('aw_height', heights, awH, minH, maxH)}
        <span className="for_sep">/</span>
        <input
          className="small-input"
          type=""
          name="od_height"
          readOnly
          value={truncDec(model?.get('od_height'))}
          onChange={() => undefined}
        />
        <span className="label">
          <span className="">Width</span> x <span className="">Height</span> (AW/OD)
        </span>
      </div>
    </li>
  );
};

const DamperForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting', 'performance'],
    autoSize: damperAutoSize,
    autoSizeGetState: (obj) => {
      try {
        const p = JSON.parse(String(obj?.damper_params ?? '{}')) as Record<string, unknown>;
        return { aw_width: p.aw_width, aw_height: p.aw_height };
      } catch {
        return {};
      }
    },
    getFaceVelocityArea: (data) => {
      try {
        const m = data.model as Record<string, unknown> | undefined;
        const p = JSON.parse(String(m?.damper_params ?? '{}')) as Record<string, unknown>;
        const w = Number(p.aw_width);
        const h = Number(p.aw_height);
        return w && h ? w * h : null;
      } catch {
        return null;
      }
    },
    defaultFaceVelocity: 1200,
  });
  const sectionId = Number(api.model?.get('section_id') ?? section.id);
  useDamperWeb(api, row.id, sectionId);
  const { internalWalls } = useSectionInternalWalls(sectionId, api.cmsAccessory);
  api.setInternalWalls(internalWalls);

  // fresh-create auto_open_collapse (§49.6:397-403 / parity 329): a damper
  // created this session (`label` row without prior subtab state) auto-opens
  // mounting + performance.
  const model = api.model;
  const freshCreated = !!(row as Record<string, unknown>).label && !api.loading;
  useEffect(() => {
    if (freshCreated) {
      api.toggleSubtab('mounting', true);
      api.toggleSubtab('performance', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshCreated]);

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const engine = api.catalogueFields.master_damper_id;
  // mounting list from the MODEL serializer (mounting_planes incl. hood
  // entries), internal_wall_* stripped + fan_intake stripped unless allowed
  // (§49.6:726-733 / parity 330).
  const modelPlanes = ((model.get('mounting_planes') as PlaneOption[]) ?? []).filter(
    (p) => !/^internal_wall_[a-z]+/.test(String(p.value ?? ''))
  );
  const cabinetPlanes = model.get('allow_fan_intake')
    ? modelPlanes
    : modelPlanes.filter((p) => String(p.value) !== 'fan_intake');
  const resolved = resolveComplexPlane(
    api,
    internalWalls,
    cabinetPlanes as Array<Record<string, unknown> & { id: number }>
  );
  const planeValueNow = String(
    api.position?.get('mounting_plane') ?? resolved.planeValue ?? ''
  );
  const isFanIntake = planeValueNow === 'fan_intake';
  const airStreams = accList(api.cmsAccessory, 'air_stream');
  const hoodBound = !!model.get('hood_id');
  const selectedValue = hoodBound
    ? String(model.get('hood_id'))
    : api.position && Number(api.position.get('mounting_plane_id'))
      ? String(api.position.get('mounting_plane_id'))
      : 'None';
  const fanIntakeNonClickable = isFanIntake ? ' non-clickable' : '';

  return (
    <>
      <AccessorySelectHeading api={api} type="damper" headingClass={String(engine?.cmsCount ?? '')} />
      <div
        className={`preloader dotted-preloader for_hood_mounting${
          api.forHoodMounting ? ' active' : ''
        }`}
      />
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist ">
          <AccSubtab api={api} type="damper" cid={cid} name="properties" sublistClass="max-height-auto non-clickable">
            <li>
              <div>
                <ABareInput api={api} name="tag" formatter="none" extraClass="text-left" />
                <i className="text-clear icon-close-1" />
                <span className="label">Tag</span>
              </div>
            </li>
            <DParamsSelect api={api} name="mfg_id" label="Manufacturer" cmsPath="manufacturer" />
            <DParamsSelect api={api} name="series_id" label="Series" cmsPath="series" labelKey="series" />
            <DParamsSelect api={api} name="leakage_id" label="Leakage" cmsPath="leakage" />
            <AwOdRow api={api} />
            <li>
              <div>
                <div className="checkboxes-group">
                  {accList(api.cmsAccessory, 'blade_orientation').map((o) => (
                    <label className="radio" key={String(o.id)}>
                      <input
                        type="radio"
                        name="blade_orientation_id"
                        value={String(o.id)}
                        checked={looseEq(o.id, engine?.paramValue('blade_orientation_id'))}
                        className="damper_params_field"
                        tabIndex={-1}
                        onChange={(e) =>
                          api.commitParamsField('master_damper_id', 'blade_orientation_id', e.target.value)
                        }
                      />
                      {String(o.name ?? '')}
                    </label>
                  ))}
                </div>
                <span className="label">Blade Orientation</span>
              </div>
            </li>
            <DParamsSelect api={api} name="frame_mtl_id" label="Frame Material" cmsPath="frame_mtl" />
            <DParamsSelect api={api} name="shaft_mtl_id" label="Shaft Material" cmsPath="shaft_mtl" />
            <li>
              <div>
                <input
                  type="text"
                  data-formatter_type="integer"
                  autoComplete="off"
                  data-min="0"
                  name="shaft_qty"
                  value={String(model.get('drive_shaft_qty') ?? '')}
                  readOnly
                  tabIndex={-1}
                  onChange={() => undefined}
                />
                <span className="label">Drive Shaft Qty</span>
              </div>
            </li>
          </AccSubtab>

          <AccSubtab
            api={api}
            type="damper"
            cid={cid}
            name="mounting"
            label="Mounting"
            liClass="mounting-section-wrapper"
            sublistClass="non-clickable"
          >
            <ComplexMountingPlaneRow
              api={api}
              cabinetPlanes={cabinetPlanes}
              internalWalls={internalWalls}
              selectionText={String(model.get('mounting_plane') ?? '')}
              selectedValue={selectedValue}
              locationBtnNonClickable={isFanIntake}
              selectClass="position_field damper_params_field selectpicker"
              selectId={String(model.get('id') ?? row.id)}
            />
            <ComplexPlaneRows
              api={api}
              variant="damper"
              planeValue={resolved.planeValue}
              iwPlane={resolved.iwPlane}
            />
            {resolved.planeValue !== 'floor' && (
              <li className={fanIntakeNonClickable ? 'non-clickable' : undefined}>
                <div>
                  <div className="checkboxes-group">
                    {accList(api.cmsAccessory, 'casing_mount').map((o) => (
                      <label className="radio" key={String(o.id)}>
                        <input
                          type="radio"
                          name="casing_mount_id"
                          value={String(o.id)}
                          checked={looseEq(o.id, model.get('casing_mount_id'))}
                          className="model_field"
                          tabIndex={-1}
                          onChange={(e) => api.commitModelField('casing_mount_id', e.target.value)}
                        />{' '}
                        {/* §JSX-WS — form.tmpl:445-446 newlines between the
                            radio and `{{name}}`; see CasingMountRow. */}
                        {String(o.name ?? '')}
                      </label>
                    ))}
                  </div>
                  <span className="label">Casing Mount</span>
                </div>
              </li>
            )}
            <BladeDirectionRow api={api} />
            <AirDirectionRow api={api} />
          </AccSubtab>

          <AccSubtab
            api={api}
            type="damper"
            cid={cid}
            name="performance"
            label="Performance"
            sublistClass="performance-sublist non-clickable"
          >
            <li>
              <div className="">
                <span className="unit">CFM</span>
                <span className={`selection${fanIntakeNonClickable}`}>
                  {airStreams.find((o) => looseEq(o.id, model.get('air_stream_id'))) ? (
                    <span>
                      {String(
                        airStreams.find((o) => looseEq(o.id, model.get('air_stream_id')))?.name ?? ''
                      )}
                    </span>
                  ) : null}
                </span>
                <select
                  className={`coli-medium model_field${fanIntakeNonClickable}`}
                  name="air_stream_id"
                  required
                  value={
                    model.get('air_stream_id') == null ? 'None' : String(model.get('air_stream_id'))
                  }
                  onChange={(e) => api.commitModelField('air_stream_id', e.target.value)}
                >
                  <option value="None">- -</option>
                  {airStreams.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.name ?? '')}&nbsp;
                    </option>
                  ))}
                </select>
                <ABareInput
                  api={api}
                  name="airflow_actual"
                  formatter="integer"
                  extraClass={`coli-medium${fanIntakeNonClickable}`}
                  min={Number(model.get('min_airflow')) || 0}
                  max={Number(model.get('max_airflow')) || 400000}
                />
                <span className="label">Air Stream &amp; Flow</span>
              </div>
            </li>
            <AutoSizeRow
              api={api}
              tooltip="Damper will be automatically sized based on 2:1 width x height ratio, airflow and desired velocity"
            />
            <li>
              <div>
                <span className="unit short-unit">fpm</span>
                <input
                  type="text"
                  data-formatter_type="integer"
                  autoComplete="off"
                  data-min="0"
                  name="face_velocity_actual"
                  className="coli-medium"
                  readOnly
                  tabIndex={-1}
                  value={String(model.get('face_velocity_actual') ?? '')}
                  onChange={() => undefined}
                />
                <span className="label">Face Velocity </span>
              </div>
            </li>
            <li>
              <div>
                <span className="unit">in WC</span>
                <input
                  type="text"
                  autoComplete="off"
                  data-min="0"
                  name="apd"
                  className="coli-xs"
                  readOnly
                  tabIndex={-1}
                  value={String(model.get('apd') ?? '')}
                  onChange={() => undefined}
                />
                <label
                  className={`for-center-position vp-for-center-position${
                    hoodBound ? ' non-clickable' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    name="include_opening_in_vp_calc"
                    className="model_field"
                    checked={!!model.get('include_opening_in_vp_calc')}
                    onChange={(e) =>
                      api.commitModelCheckbox('include_opening_in_vp_calc', e.target.checked)
                    }
                  />
                  <span>Include in the Fan VP Calculation</span>
                </label>
                <span className="label">Air Pressure Drop </span>
              </div>
            </li>
          </AccSubtab>
        </ul>
      </form>
      <div id={`popup_accessory_mounting_${row.id}`} className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
      <AccessoryMountingPopup
        api={api}
        typeName="damper"
        typeValue="damper"
        SpecialParameters={DamperSpecialParameters}
      />
    </>
  );
};

export default DamperForm;
